import React from 'react';
import { format, isToday } from 'date-fns';
import LessonCard from './LessonCard';
import { useLocale } from '../../context/LocaleContext';
import {
  HOURS, HOUR_HEIGHT, PIXELS_PER_MINUTE, DAY_MINUTES,
  minuteToDate, formatDuration,
} from './weekGrid';
import s from './WeekView.module.css';

// One day's column: the hour lines behind it, the lessons on it, and the ghost
// while something is being dragged.
//
// `placed` is the column's blocks, one per occurrence, each with the core's
// `place` (`{ startMin, endMin, lane, lanes }`, WeekView.jsx): its top is its
// minute from midnight, and it takes `1 / lanes` of the width at lane `lane`
// (#749). The column is the whole day, so every block is drawn.
export default function WeekDayColumn({
  day, dayIndex, placed, categoryColorFor, colRef, ghost, onPointerDown,
  onLessonClick, onResizeStart, onMoveStart, liveDurationFor, isMoving,
}) {
  // The ghost's range in the Region's clock, without the day period the card
  // under it will carry (#534): `4:00 – 4:30` here, `16:00 – 16:30` in Germany.
  const { clockParts } = useLocale();
  const clock = (min) => clockParts(format(minuteToDate(day, min), 'HH:mm')).time;
  return (
    <div
      ref={colRef}
      className={`${s.weekDayCol}${isToday(day) ? ` ${s.todayCol}` : ''}`}
      onPointerDown={(e) => onPointerDown(dayIndex, day, e)}
    >
      {HOURS.map((hour) => {
        const top = hour * HOUR_HEIGHT;
        return (
          <React.Fragment key={hour}>
            <div className={s.weekHourLine} style={{ top }} />
            <div className={`${s.weekHourLine} ${s.half}`} style={{ top: top + HOUR_HEIGHT / 2 }} />
            {/* Non-interactive: it only hints "clickable" before the
                pointerdown on the column fires. */}
            <div className={s.weekSlotClickable} style={{ top, height: HOUR_HEIGHT }} />
          </React.Fragment>
        );
      })}

      {ghost ? (
        <div
          className={s.weekGhost}
          style={{
            top: ghost.startMin * PIXELS_PER_MINUTE,
            height: ghost.duration * PIXELS_PER_MINUTE - 2,
          }}
        >
          {/* A lesson being moved says whose it is and where it would land;
              a new one says how long it would be (#571). */}
          <div className={s.weekGhostTitle}>
            {ghost.moving
              ? (ghost.moving.studentName || ghost.moving.categoryLabel || 'Lesson')
              : `New lesson · ${ghost.duration} min`}
          </div>
          <div className={s.weekGhostRange}>
            {clock(ghost.startMin)} – {clock(ghost.endMin)}
          </div>
        </div>
      ) : null}

      {placed.map(({ lesson, place }) => {
        const { duration, resizing } = liveDurationFor(lesson);
        const top = place.startMin * PIXELS_PER_MINUTE;
        // The live length while it is being resized, else the core's span —
        // an hour for a length of none, and cut at midnight either way.
        const minutes = resizing ? duration : place.endMin - place.startMin;
        const height = Math.min(minutes, DAY_MINUTES - place.startMin) * PIXELS_PER_MINUTE;
        const moving = isMoving?.(lesson);
        return (
          <div
            key={lesson.id}
            className={moving ? s.weekLessonMoving : s.weekLessonWrapper}
            style={{ top, height, '--lane': place.lane, '--lanes': place.lanes }}
          >
            <LessonCard
              lesson={lesson}
              categoryColorFor={categoryColorFor}
              onClick={(e) => onLessonClick?.(lesson, e.currentTarget)}
              onResizeStart={onResizeStart}
              onMoveStart={onMoveStart}
              // Half or a third of a column: time, name and category a line
              // each, as MMS keeps them in a half-width block.
              narrow={place.lanes > 1}
              style={{ height: '100%' }}
            />
            {resizing ? (
              <span className={s.weekResizePill} style={{ top: height + 4 }}>
                {formatDuration(duration)}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
